import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { api } from '../../services/api';
import { getPublishedQuestions, AssessmentQuestionDTO } from '../../services/tutorAssessmentService';

interface SubmissionFile {
  id: number;
  originalFilename: string;
  contentType: string;
  fileSizeBytes: number;
  presignedUrl: string;
  uploadedAt: string;
}

interface Evaluation {
  feedbackText?: string;
  mark: number;
  totalMarks: number;
  evaluatedAt: string;
}

interface Submission {
  id: number;
  status: string;
  files: SubmissionFile[];
  evaluation?: Evaluation;
}

interface StudentAssessmentViewDTO {
  questions: AssessmentQuestionDTO[];
  submission?: Submission;
}

const MAX_FILE_SIZE_MB = 20;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

export default function StudentAssessmentSection({ courseId, nodeId }: { courseId: number; nodeId: number }) {
  const [view, setView] = useState<StudentAssessmentViewDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ data: StudentAssessmentViewDTO }>(
        `/api/student/courses/${courseId}/topics/${nodeId}/assessment`
      );
      setView(res.data.data);
    } catch { /* handled */ } finally { setLoading(false); }
  }, [courseId, nodeId]);

  useEffect(() => { load(); }, [load]);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileError('');

    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError('Only JPEG, PNG, and PDF files are accepted.');
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setFileError(`File must be under ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/api/student/courses/${courseId}/topics/${nodeId}/assessment/files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
    } catch { setFileError('Upload failed. Please try again.'); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  if (loading) return <CircularProgress size={20} />;
  if (!view || view.questions.length === 0) return null;

  const { questions, submission } = view;
  const evaluation = submission?.evaluation;

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Assessment Questions</Typography>
      <Stack spacing={1} mb={2}>
        {questions.map((q, i) => (
          <Paper key={q.id} variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2" fontWeight={500}>{i + 1}. {q.questionText}</Typography>
            <Typography variant="caption" color="text.secondary">{q.allocatedMarks} marks</Typography>
          </Paper>
        ))}
      </Stack>

      {evaluation && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            Evaluated: {evaluation.mark}/{evaluation.totalMarks} marks
          </Typography>
          {evaluation.feedbackText && (
            <Typography variant="body2">{evaluation.feedbackText}</Typography>
          )}
        </Alert>
      )}

      {submission?.files && submission.files.length > 0 && (
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>Your Submitted Files</Typography>
          <Stack spacing={1}>
            {submission.files.map(f => (
              <Paper key={f.id} variant="outlined" sx={{ p: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">{f.originalFilename}</Typography>
                  <Button size="small" href={f.presignedUrl} target="_blank" rel="noreferrer">
                    View
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" gutterBottom>Submit Your Work</Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        Upload JPEG, PNG, or PDF files (max 20 MB each). You can upload multiple files.
        {submission?.status === 'EVALUATED' && ' Adding new files after evaluation will reset your submission for re-review.'}
      </Typography>
      {fileError && <Alert severity="error" sx={{ mb: 1 }}>{fileError}</Alert>}
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      <Button
        variant="outlined"
        startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
      >
        {uploading ? 'Uploading…' : 'Upload File'}
      </Button>
    </Box>
  );
}
